import React, {useState} from 'react'

// Components
import {CheckGiftcardCode} from './check-giftcard-code'
import {
    Button,
    FormErrorMessage,
    FormLabel,
    Heading,
    Input,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Chakra adds role="group" unconditionally; this wrapper omits it by default
import FormControl from '@app/components/form-control'

// Hooks
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useForm} from 'react-hook-form'
import {useGiftCardActions} from '@app/hooks/use-gift-card-actions'
import {useIntl} from 'react-intl'

/**
 * Partial component for checking the balance of a gift card, on the gift card page.
 *
 * @returns {JSX.Element}
 */
export const CheckGiftcardForm = () => {
    const {
        handleSubmit,
        register,
        resetField,
        formState: {errors: errorsCheck, isSubmitting: isSubmittingCheck}
    } = useForm({mode: 'all'})
    const {data: basket} = useCurrentBasket()
    const intl = useIntl()
    const {checkGiftCardBalance} = useGiftCardActions(basket)

    /**
     * When giftcardCode is set, <CheckGiftcardCode /> automatically makes a request to get its balance.
     */
    const [gcCode, setGcCode] = useState('')
    const [checkGiftCardData, setCheckGiftCardData] = useState({})

    /**
     * Handles the submission of the gift card form.
     * @param {Object} values - The form values containing the gift card code.
     * @param {string} values.giftcardCode - The code of the gift card entered by the user.
     */
    const onSubmit = async (values) => {
        setGcCode(values.giftcardCode)
        setCheckGiftCardData(await checkGiftCardBalance(values.giftcardCode))
    }

    /**
     * Clears the gift card input field by resetting its value.
     * This function is typically used to clear the gift card code input field in a form.
     */
    const clearGiftcard = () => {
        setGcCode('')
        setCheckGiftCardData({})
        resetField('giftcardCode')
    }

    return (
        <form
            id="giftcard-check-form"
            data-testid="sf-check-giftcard-check-form"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
        >
            <Text variant="eyeBrowSmall" textTransform="uppercase" mb="8">
                {intl.formatMessage({
                    id: 'giftcard.form.check.subtitle',
                    defaultMessage: 'Already have an online gift card?'
                })}
            </Text>
            <Heading size="4xl" textTransform="uppercase" mb="16">
                {intl.formatMessage({
                    id: 'giftcard.form.check.title',
                    defaultMessage: 'Check the balance of an online gift card'
                })}
            </Heading>
            <Text variant="bodyBase2" mb="24">
                {intl.formatMessage({
                    id: 'giftcard.form.check.text',
                    defaultMessage:
                        'To check the balance of your online gift card, please enter your gift card code below:'
                })}
            </Text>
            <FormControl variant="floating" isInvalid={errorsCheck.giftcardCode} isRequired mb="24">
                <Input
                    id="giftcardCode"
                    name="giftcardCode"
                    variant="basic"
                    placeholder={intl.formatMessage({
                        id: 'giftcard.form.check.giftcardCode.placeholder',
                        defaultMessage: 'Enter gift card code'
                    })}
                    {...register('giftcardCode', {
                        required: intl.formatMessage({
                            id: 'giftcard.form.required',
                            defaultMessage: 'This field is required'
                        })
                    })}
                />
                <FormLabel htmlFor="giftcardCode">
                    {intl.formatMessage({
                        id: 'giftcard.form.check.giftcardCode.label',
                        defaultMessage: 'Gift Card Code'
                    })}
                </FormLabel>
                <FormErrorMessage>{errorsCheck?.giftcardCode?.message}</FormErrorMessage>

                <CheckGiftcardCode
                    code={gcCode}
                    checkGiftCardData={checkGiftCardData}
                    onClose={clearGiftcard}
                />
            </FormControl>

            <Button type="submit" isLoading={isSubmittingCheck} variant={'primary'}>
                {intl.formatMessage({
                    id: 'giftcard.form.check.button',
                    defaultMessage: 'Check your balance'
                })}
            </Button>
        </form>
    )
}
